import ThemedDialog from 'Components/_NewElements/Dialog';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import { exists } from 'neuro-utils';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'Store/index';
import { attachmentFetchBuilder } from 'Store/messaging/fetchers/attachment';
import { topicFetchBuilder } from 'Store/messaging/fetchers/topics';
import { actions as sessionActions } from 'Store/session';
import colors from '../../../config/theme/colors';
import GroupMessageForm from './GroupMessageForm';
import PatientList from './PatientList';

const GroupMessageFormHandler = ({ selectedPlatform, moveToGroupMessages }: IGroupMessageForm) => {
  const dispatch = useAppDispatch();

  const formValues = useAppSelector(s => s.session.messagingSessionSettings.newGroupMessage);
  const userId = useAppSelector(s => s.session.data?.useruuid);

  const onChange = (name: string) => (values: TOnChangeValues) => {
    const value = values[name];
    dispatch(sessionActions.setMessagesSessionSettings({ newGroupMessage: { ...formValues, [name]: value } }));
  };

  const [attachmentData, setAttachmentData] = React.useState<File[]>([]);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachmentData(prev => (e.target.files?.[0] ? [...prev, e.target.files[0]] : prev));
    }
  };

  const stateGroupMessagePatients = useAppSelector(s => s.session.messagingSessionSettings.groupMessagePatients);
  const cancelGroupMessage = () =>
    dispatch(sessionActions.setMessagesSessionSettings({ groupMessagePatients: null, newGroupMessage: null }));

  const sendGroupMessage = async () => {
    if (!formValues?.title || !formValues.body || !selectedPlatform) return;
    topicFetchBuilder<'create'>({
      name: 'create',
      body: {
        title: formValues.title,
        body: formValues.body,
        targetPatients: (stateGroupMessagePatients ?? []).map(pat => pat.patientId),
        tags: [selectedPlatform],
      },
    }).then(async res => {
      if (res) {
        if (res.id) {
          await topicFetchBuilder<'activate'>({
            name: 'activate',
            queryParams: [res.id],
          });
          await topicFetchBuilder<'update'>({
            name: 'update',
            body: { allowThreads: false },
            queryParams: [res.id],
          });
          userId &&
            (await topicFetchBuilder<'assign'>({
              name: 'assign',
              queryParams: [res.id, userId],
            }));
        }
        if (res.id) {
          attachmentData.forEach(async att => {
            await attachmentFetchBuilder<'createTopic'>({
              name: 'createTopic',
              queryParams: [res.id],
              body: att,
              contentType: att.type,
            });
          });
        }
        cancelGroupMessage();
        moveToGroupMessages?.();
      }
    });
  };

  return (
    <>
      <GroupMessageForm
        formValues={formValues}
        onChange={onChange}
        sendGroupMessage={sendGroupMessage}
        cancelGroupMessage={cancelGroupMessage}
        attachmentData={attachmentData}
        handleFileChange={handleFileChange}
        existingAttachments={null}
      />
    </>
  );
};

interface IGroupMessageForm {
  selectedPlatform?: Platform;
  moveToGroupMessages?: () => void;
}

const GroupMessagingDialogs = ({ selectedPlatform, moveToGroupMessages }: IGroupMessagingDialogs) => {
  const { formatMessage } = useIntl();
  const fm = (id: string, values?: Record<string, any>) => formatMessage({ id }, values);

  const dispatch = useAppDispatch();

  const searchResults = useAppSelector((state: IState) => state.patientsearch.searchResults);

  const stateGroupMessagePatients = useAppSelector(s => s.session.messagingSessionSettings.groupMessagePatients);
  const cancelGroupMessage = () =>
    dispatch(sessionActions.setMessagesSessionSettings({ groupMessagePatients: null, newGroupMessage: null }));

  const stateGroupMessageFormContent = useAppSelector(s => s.session.messagingSessionSettings.newGroupMessage);
  const openGroupMessageForm = () =>
    dispatch(sessionActions.setMessagesSessionSettings({ newGroupMessage: { title: '', body: '' } }));

  const sortedGroupMessagePatients = (stateGroupMessagePatients ?? []).toSorted((a, b) =>
    a.lastNames > b.lastNames ? 1 : a.lastNames < b.lastNames ? -1 : 0,
  );

  return (
    <>
      <ConfirmationDialog
        width="sm"
        open={!!stateGroupMessagePatients && !exists(stateGroupMessageFormContent?.title)}
        text={
          <div style={{ width: '100%' }}>
            <h2 style={{ color: colors.primary }}>{fm('messaging.groupMessage')}</h2>
            <div style={{ fontSize: '1.6rem', color: colors.black, width: '100%' }}>
              {(stateGroupMessagePatients ?? []).length === 0 ? (
                <>{fm('messaging.noPatientsForGroupMessage')}</>
              ) : (
                <div>
                  {fm('messaging.groupMessageValidPatients', {
                    count: (stateGroupMessagePatients ?? []).length,
                    totalCount: (searchResults ?? []).length,
                  })}
                  <br />
                  <br />
                  <PatientList patientIdList={sortedGroupMessagePatients.map(s => s.patientId)} />
                </div>
              )}
            </div>
          </div>
        }
        cancel={{ callback: cancelGroupMessage }}
        confirm={{
          text: 'messaging.groupMessageCreation',
          callback: openGroupMessageForm,
          disabled: (stateGroupMessagePatients ?? []).length <= 1,
          disabledTooltip: <>{fm('messaging.groupMessageConfirmDisabledTooltip')}</>,
        }}
      />
      <ThemedDialog
        open={exists(stateGroupMessageFormContent?.title)}
        closeOnOutsideClick={false}
        title={fm('messaging.groupMessage')}
      >
        <GroupMessageFormHandler selectedPlatform={selectedPlatform} moveToGroupMessages={moveToGroupMessages} />
      </ThemedDialog>
    </>
  );
};

interface IGroupMessagingDialogs {
  selectedPlatform?: Platform;
  moveToGroupMessages?: () => void;
}

export default GroupMessagingDialogs;
